import { useState } from 'react'
import { LanguageFlag } from '@/components/ui/LanguageFlag'
import { cn } from '@/utils/cn'

interface GlossWord {
  term: string
  def: string
}

interface Segment {
  type: 'text' | 'word'
  content: string
  gloss?: GlossWord
}

const PASSAGE: Segment[] = [
  { type: 'text', content: 'Om morgenen gikk hun ned til ' },
  { type: 'word', content: 'vannet', gloss: { term: 'vannet', def: 'the lake; the water' } },
  { type: 'text', content: '. Luften var kald og ' },
  { type: 'word', content: 'still', gloss: { term: 'still', def: 'quiet, calm, still' } },
  { type: 'text', content: ', og ' },
  { type: 'word', content: 'fjellene', gloss: { term: 'fjellene', def: 'the mountains' } },
  { type: 'text', content: ' lå mørke mot himmelen. Hun satte seg på en stein og ' },
  { type: 'word', content: 'ventet', gloss: { term: 'ventet', def: 'waited' } },
  { type: 'text', content: ' på at solen skulle komme.' },
]

const DEFAULT_ACTIVE: GlossWord = { term: 'still', def: 'quiet, calm, still' }

function GlossSpecimen() {
  const [active, setActive] = useState<GlossWord>(DEFAULT_ACTIVE)

  return (
    <figure
      className="bg-surface-bg border border-line-soft rounded-[4px] px-5 py-8 sm:px-10 sm:pt-[38px] sm:pb-[34px]"
      style={{
        boxShadow: 'var(--shadow-card-inset)',
      }}
      aria-label="A glossable reading passage in Norwegian"
    >
      <div className="flex items-center justify-between border-b border-line-soft pb-3.5 mb-5">
        <span className="flex items-center gap-2 font-mono text-xs text-muted">
          <LanguageFlag isoCode="no" size="m" />
          Norsk · Lesestykke
        </span>
        <span className="font-mono text-[11px] uppercase tracking-widest text-accent font-semibold">
          A2 · Beginner
        </span>
      </div>

      <p className="font-serif text-xl leading-relaxed text-body">
        {PASSAGE.map((seg, i) =>
          seg.type === 'text' ? (
            <span key={i}>{seg.content}</span>
          ) : (
            <span
              key={i}
              role="button"
              tabIndex={0}
              className={cn(
                'border-b border-dotted border-accent cursor-help rounded-[2px] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
                active.term === seg.gloss?.term
                  ? 'bg-accent/[0.12]'
                  : 'hover:bg-accent/[0.08]',
              )}
              onClick={() => seg.gloss && setActive(seg.gloss)}
              onKeyDown={(e) => e.key === 'Enter' && seg.gloss && setActive(seg.gloss)}
              onMouseEnter={() => seg.gloss && setActive(seg.gloss)}
            >
              {seg.content}
            </span>
          ),
        )}
      </p>

      <div className="mt-5 border-t border-line-soft pt-4 flex items-baseline gap-4 min-h-[30px]">
        <span className="font-serif italic text-[19px] text-accent whitespace-nowrap">
          {active.term}
        </span>
        <span className="w-4 shrink-0 self-center" style={{ height: '1px', background: 'var(--color-border-divider)' }} />
        <span className="font-serif text-base text-muted">{active.def}</span>
      </div>
    </figure>
  )
}

export function ProofSection() {
  return (
    <section className="relative z-10 py-14 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[0.82fr_1.18fr] gap-8 lg:gap-16 items-center">
          <div>
            <span className="block font-mono text-xs uppercase tracking-widest text-accent mb-4">
              How it reads
            </span>
            <h2 className="font-display text-2xl sm:text-3xl leading-tight text-heading mb-4">
              Meaning, the moment you need it.
            </h2>
            <p className="font-serif text-base leading-relaxed text-muted max-w-[38ch]">
              Tap any underlined word and its sense — in this sentence, not a dictionary's longest
              guess — appears beneath the line. Then you read on, uninterrupted.
            </p>
          </div>
          <GlossSpecimen />
        </div>
      </div>
    </section>
  )
}
